// BM-H398 - pick a person from the couple's people register, or add one.
//
// Every individual the will names lives once in `couple.people_register` and
// is referenced by id everywhere else, which is what the engine expects (it
// resolves `person_id` through `getPerson`).

import { useState } from "react";

import { QuestionRow, Select, SmallButton, TextInput, YesNo, inputStyle } from "./fields";
import { newPerson } from "./willsPayload";

export const RELATIONSHIP_OPTIONS = [
  { value: "spouse", label: "Spouse" },
  { value: "civil_partner", label: "Civil partner" },
  { value: "partner", label: "Partner (not married)" },
  { value: "child", label: "Child" },
  { value: "stepchild", label: "Stepchild" },
  { value: "grandchild", label: "Grandchild" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Brother or sister" },
  { value: "friend", label: "Friend" },
  { value: "other", label: "Other" },
];

// BM-H4005: the Will prints "my <relationship>" for the person who takes the estate if everything else fails and for each
// person it names as left out, and "Other" cannot be printed that way (backend/hub/wills_assembly/longstop.py and
// exclusion_naming.py both refuse it). Where the relationship is needed the form offers only the values the Will can
// state, starts with none chosen, and will not save a new person until one is chosen.
export const REQUIRED_RELATIONSHIP_OPTIONS = RELATIONSHIP_OPTIONS.filter((o) => o.value !== "other");
export const RELATIONSHIP_PROMPT = "Choose how they are related to you";

export function relationshipIsStatable(person) {
  return REQUIRED_RELATIONSHIP_OPTIONS.some((o) => o.value === person?.relationship);
}

// BM-H3960 item 32: edit one part of a person's address and keep the others. The first line used to rebuild the whole address
// with country "United Kingdom"; now nothing is stamped, and an address with neither a first line nor a country is no address.
export function personAddress(person, patch) {
  const next = { line1: "", line2: null, town: "", county: "", postcode: "", country: "", ...(person.address || {}), ...patch };
  return next.line1 || next.country ? next : null;
}

export function PersonEditor({ person, onChange, testidPrefix = "person", showDob = true, showCapacity = false, relationshipRequired = false }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <QuestionRow qid="person.full_legal_name" label="Full legal name" testid={`${testidPrefix}-q-name`}>
        <TextInput testid={`${testidPrefix}-name`} value={person.full_legal_name} onChange={(v) => onChange({ ...person, full_legal_name: v })} />
      </QuestionRow>
      <QuestionRow qid="person.relationship" label="Relationship to you" testid={`${testidPrefix}-q-relationship`}>
        {relationshipRequired ? (
          <Select testid={`${testidPrefix}-relationship`} value={relationshipIsStatable(person) ? person.relationship : null} options={REQUIRED_RELATIONSHIP_OPTIONS} placeholder={RELATIONSHIP_PROMPT} onChange={(v) => onChange({ ...person, relationship: v })} />
        ) : (
          <Select testid={`${testidPrefix}-relationship`} value={person.relationship} options={RELATIONSHIP_OPTIONS} onChange={(v) => onChange({ ...person, relationship: v || "other" })} />
        )}
      </QuestionRow>
      {showDob && (
        <QuestionRow qid="person.date_of_birth" label="Date of birth" testid={`${testidPrefix}-q-dob`}>
          <TextInput type="date" testid={`${testidPrefix}-dob`} value={person.date_of_birth} onChange={(v) => onChange({ ...person, date_of_birth: v || null })} />
        </QuestionRow>
      )}
      <QuestionRow qid="person.address_line1" label="Address (first line and postcode)" testid={`${testidPrefix}-q-address`}>
        <TextInput
          testid={`${testidPrefix}-address`}
          value={person.address?.line1}
          onChange={(v) => onChange({ ...person, address: personAddress(person, { line1: v }) })}
        />
      </QuestionRow>
      {/* BM-H3960 item 32: the country is no longer stamped "United Kingdom". Blank means a UK address with a UK postcode. */}
      <QuestionRow qid="person.address_country" label="Country (only if the address is outside the United Kingdom)" testid={`${testidPrefix}-q-country`}>
        <TextInput
          testid={`${testidPrefix}-country`}
          value={person.address?.country}
          onChange={(v) => onChange({ ...person, address: personAddress(person, { country: v }) })}
        />
      </QuestionRow>
      {showCapacity && (
        <QuestionRow qid="person.has_capacity_concerns" label="Is there any concern about this person managing their own affairs?" sensitive testid={`${testidPrefix}-q-capacity`}>
          <YesNo testid={`${testidPrefix}-capacity`} value={person.has_capacity_concerns} onChange={(v) => onChange({ ...person, has_capacity_concerns: v })} />
        </QuestionRow>
      )}
    </div>
  );
}

// BM-H3931: `excludeUnlessSelected` hides people from the choices but keeps one that is ALREADY chosen in this row, so a
// saved answer that is no longer offered stays visible (and can be flagged) instead of silently showing "Choose a person".
export default function PersonPicker({ payload, update, value, onChange, qid, label, exclude = [], excludeUnlessSelected = [], allowAdd = true, notKnown, onNotKnown, testid, defaultRelationship = "other", relationshipRequired = false }) {
  const [adding, setAdding] = useState(false);
  // BM-H4006: a person already in the register could not be corrected from the picker (only a new one could be filled in), so a
  // wrong country on a longstop or excluded person's address was a dead end. "Edit details" opens the same fields on the record.
  const [editing, setEditing] = useState(false);
  // BM-H4005: when the relationship is needed the new person starts with none chosen (null), never with "Other".
  const blankPerson = () => newPerson({ relationship: relationshipRequired ? null : defaultRelationship });
  const [draft, setDraft] = useState(blankPerson);
  const [showNeed, setShowNeed] = useState(false);
  const people = (payload.couple.people_register || []).filter((p) => !exclude.includes(p.id) && (!excludeUnlessSelected.includes(p.id) || p.id === value));
  const tid = testid || `picker-${qid}`;

  const chosenPerson = value ? (payload.couple.people_register || []).find((p) => p.id === value) : null;
  const chosenNeedsRelationship = relationshipRequired && chosenPerson && !relationshipIsStatable(chosenPerson);

  function setChosenRelationship(rel) {
    update((d) => {
      const p = d.couple.people_register.find((x) => x.id === value);
      if (p) p.relationship = rel || "other";
    });
  }

  const selected = chosenPerson;

  function editSelected(next) {
    update((d) => {
      const i = d.couple.people_register.findIndex((p) => p.id === next.id);
      if (i >= 0) d.couple.people_register[i] = next;
    });
  }

  function commitAdd() {
    if (!draft.full_legal_name.trim()) return;
    if (relationshipRequired && !relationshipIsStatable(draft)) {
      setShowNeed(true);
      return;
    }
    const person = { ...draft, full_legal_name: draft.full_legal_name.trim() };
    update((d) => {
      d.couple.people_register.push(person);
    });
    onChange(person.id);
    setDraft(blankPerson());
    setShowNeed(false);
    setAdding(false);
  }

  return (
    <QuestionRow qid={qid} label={label} notKnown={notKnown} onNotKnown={onNotKnown} testid={`q-${tid}`}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select data-testid={tid} style={{ ...inputStyle, flex: 1, minWidth: 200 }} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Choose a person</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>{p.full_legal_name || "(unnamed)"}</option>
          ))}
        </select>
        {allowAdd && (
          <SmallButton testid={`${tid}-add`} onClick={() => setAdding((v) => !v)}>
            {adding ? "Cancel" : "Add someone"}
          </SmallButton>
        )}
        {selected && (
          <SmallButton testid={`${tid}-edit`} onClick={() => setEditing((v) => !v)}>
            {editing ? "Done" : "Edit details"}
          </SmallButton>
        )}
      </div>
      {editing && selected && (
        <div data-testid={`${tid}-edit-form`} style={{ marginTop: 10, padding: 12, border: "1px dashed currentColor", borderRadius: 8 }}>
          <PersonEditor person={selected} onChange={editSelected} testidPrefix={`${tid}-edit-person`} relationshipRequired={relationshipRequired} />
        </div>
      )}
      {adding && (
        <div data-testid={`${tid}-add-form`} style={{ marginTop: 10, padding: 12, border: "1px dashed currentColor", borderRadius: 8 }}>
          <PersonEditor person={draft} onChange={setDraft} testidPrefix={`${tid}-new`} relationshipRequired={relationshipRequired} />
          {showNeed && !relationshipIsStatable(draft) && (
            <p role="alert" data-testid={`${tid}-relationship-needed`} style={{ fontSize: 13, margin: "8px 0", color: "#b45309" }}>
              Please choose how this person is related to you. Your Will has to say so, and it cannot say "other".
            </p>
          )}
          <SmallButton testid={`${tid}-add-commit`} onClick={commitAdd}>Save this person</SmallButton>
        </div>
      )}
      {chosenNeedsRelationship && (
        <div data-testid={`${tid}-relationship-fix`} style={{ marginTop: 10 }}>
          <QuestionRow qid="person.relationship" label={`How is ${chosenPerson.full_legal_name || "this person"} related to you?`} hint="Your Will has to say how they are related to you." testid={`${tid}-relationship-fix-row`}>
            <Select testid={`${tid}-relationship-fix-select`} value={null} options={REQUIRED_RELATIONSHIP_OPTIONS} placeholder={RELATIONSHIP_PROMPT} onChange={setChosenRelationship} />
          </QuestionRow>
        </div>
      )}
    </QuestionRow>
  );
}

// Several people at once (executors, guardians, a beneficiary class).
export function PeopleList({ payload, update, items, onChange, qid, label, itemQid, testid, exclude = [], excludeUnlessSelected = [], notKnown, onNotKnown, renderExtra, relationshipRequired = false }) {
  const tid = testid || `people-${qid}`;
  const chosen = items.map((x) => x.person_id).filter(Boolean);
  return (
    <div data-testid={tid}>
      {items.map((item, i) => (
        <div key={i} data-testid={`${tid}-row-${i}`} style={{ marginBottom: 8 }}>
          <PersonPicker
            payload={payload}
            update={update}
            qid={itemQid || qid}
            label={i === 0 ? label : `${label} (${i + 1})`}
            value={item.person_id}
            exclude={[...exclude, ...chosen.filter((id) => id !== item.person_id)]}
            excludeUnlessSelected={excludeUnlessSelected}
            relationshipRequired={relationshipRequired}
            onChange={(pid) => onChange(items.map((x, j) => (j === i ? { ...x, person_id: pid } : x)))}
            testid={`${tid}-picker-${i}`}
            notKnown={i === 0 ? notKnown : undefined}
            onNotKnown={i === 0 ? onNotKnown : undefined}
          />
          {renderExtra && renderExtra(item, i)}
          <SmallButton testid={`${tid}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</SmallButton>
        </div>
      ))}
      {items.length === 0 && (
        <QuestionRow qid={itemQid || qid} label={label} notKnown={notKnown} onNotKnown={onNotKnown} testid={`q-${tid}-empty`}>
          <p style={{ fontSize: 13, margin: 0 }}>No one added yet.</p>
        </QuestionRow>
      )}
      <SmallButton testid={`${tid}-add`} onClick={() => onChange([...items, { person_id: null }])}>Add a person</SmallButton>
    </div>
  );
}
